/**
 * WHAT THE MANUAL INVOICE SCREEN ADDS UP — THE VAT IS INSIDE THE PRICE.
 *
 * The /inv admin screen quotes a figure the user approves with "Илгээх", and
 * `de-invoice-service` `logics/invoice/create.ts` then recomputes that figure
 * server-side from the same lines. Two independent implementations of one sum
 * is how a screen ends up approving 1.10 x P x Q while the backend stores
 * P x Q, so this module is the screen's single copy of the arithmetic and it is
 * written to mirror the handler line for line.
 *
 * ── THE VAT ─────────────────────────────────────────────────────────────
 *
 * The price on a line is the price service's `finalPrice` — the SAME catalogue
 * price the order path treats as VAT-INCLUSIVE. The screen used to compute
 *
 *     vat = price * (vatPercent / 100) * quantity        // VAT ON TOP
 *
 * and add it into the total, which billed the VAT twice: a line at 122,900 was
 * quoted as 135,190. The VAT is EXTRACTED out of the price instead,
 *
 *     vat = price * vatPercent / (100 + vatPercent)      // VAT INSIDE
 *
 * and the grand total does NOT add it, because it is already in there. This is
 * `de-invoice-service` `logics/invoice/helpers/manual-line-vat.ts` and the
 * `{ vatMode: "INCLUSIVE" }` its two callers now pass to the discount
 * allocator. The VAT figure the screen shows is therefore informational — the
 * portion of the total that is VAT — not an addend, which is why the summaries
 * print it BELOW the total rather than in the column that sums to it.
 *
 * ── THE DEFAULTS ARE THE HANDLER'S, NOT AN OPINION ──────────────────────
 *
 * `create.ts` reads `variant.vatType || "STANDARD_RATED"` and
 * `variant.vatPercent || 10`, so a variant that says nothing about VAT is still
 * invoiced as standard-rated at 10%. Every `de_business.business_variant` row
 * on DEV says nothing (971 of 971 have `vat_type` and `vat_percent` NULL), so
 * without these defaults the screen would print "VAT 0.00" for an invoice whose
 * stored `vat_amount` is 11,172.73. The totals are unaffected either way — that
 * is the point of an inclusive model — but the VAT line would be a lie.
 *
 * ── CITY TAX IS READ, NEVER DERIVED ─────────────────────────────────────
 *
 * НХАТ is dead on this path: `apis/de-inventory-service.ts` returns a hardcoded
 * `taxAmount: 0` "whatever the variant's own hasTax / taxPercent say", and the
 * handler stores what it is handed. So the screen totals the tax AMOUNTS a line
 * actually carries and never recomputes one from `taxPercent` — deriving it
 * would put a tax in the quote that the invoice will not charge, which is the
 * VAT bug again wearing a different hat.
 *
 * PURE. No React, no store, no I/O — the caller does the reading.
 */

/** The VAT type `logics/invoice/create.ts` assumes when a variant is silent. */
const DEFAULT_VAT_TYPE = "STANDARD_RATED";

/** The rate it assumes when a variant carries none. */
const DEFAULT_VAT_PERCENT = 10;

/** The one VAT type whose lines carry VAT — every other type extracts 0. */
const STANDARD_RATED = "STANDARD_RATED";

export type InvoiceTotals = {
  /** Lines, at price x quantity less each line's own discount. */
  itemsSubtotal: number;
  /** Hand-typed lines, same basis. */
  additionalSubtotal: number;
  /** The two together — the base the invoice-level discount works from. */
  subtotal: number;
  /** The VAT CONTAINED IN `subtotal`, after the invoice-level discount. */
  vatTotal: number;
  /** City tax as stored on the lines. 0 on this path — see the header. */
  taxTotal: number;
  /** The invoice-level discount, clamped the way the handler clamps it. */
  discountAmount: number;
  shippingAmount: number;
  /** What `de_invoice.invoice.total_amount` will hold. */
  grandTotal: number;
  /** Lines plus hand-typed lines. */
  lineCount: number;
};

const num = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : parseFloat(`${value ?? ""}`);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The share of a VAT-INCLUSIVE amount that IS the VAT: `p / (100 + p)`.
 *
 * Mirrors `manualLineVatPerUnit` in de-invoice-service, including its guard:
 * only STANDARD_RATED carries VAT, and a rate of 0 carries none. Expressed as a
 * ratio rather than a per-unit figure so the one caller can apply it to the
 * line total, which is the amount the VAT is actually contained in.
 */
function vatShare(vatType: unknown, vatPercent: unknown): number {
  const type = vatType || DEFAULT_VAT_TYPE;
  const percent = num(vatPercent) || DEFAULT_VAT_PERCENT;

  if (type !== STANDARD_RATED || percent <= 0) return 0;

  return percent / (100 + percent);
}

/**
 * The discount a `discountType` / `discountValue` pair takes off `base`.
 *
 * A type the handler's `switch` has no `case` for takes off NOTHING, which is
 * why this is not a ternary: falling back to "percentage" would quietly
 * discount a line the invoice will charge in full.
 */
function discountOff(base: number, discountType: unknown, discountValue: unknown): number {
  if (!discountType || !discountValue) return 0;

  const value = num(discountValue);

  if (discountType === "AMOUNT") return value;
  if (discountType === "PERCENTAGE") return (base / 100) * value;

  return 0;
}

/** price x quantity less the line's own discount — the handler's `LINE_TOTAL`. */
export function lineTotal(line: any): number {
  const total = num(line?.price) * num(line?.quantity);

  return total - discountOff(total, line?.discountType, line?.discountValue);
}

/**
 * The VAT contained in one line, AFTER that line's own discount.
 *
 * Not `lineVatPerUnit(price) * quantity`: a line discount lowers the amount
 * actually charged, and the VAT inside it falls with it. Server-side the
 * allocator's first pass re-extracts each line's VAT from its post-discount
 * total for exactly this reason, so a screen that extracted from the
 * undiscounted `price x quantity` would over-state the VAT on any discounted
 * line while the total beside it was already net.
 */
export function lineVat(line: any): number {
  return lineTotal(line) * vatShare(line?.vatType, line?.vatPercent);
}

/**
 * Every figure the manual invoice screen prints, from one set of form values.
 *
 * The order of operations is the handler's: line discounts first, then the
 * invoice-level discount off the combined subtotal, then shipping. The VAT is
 * never added — it rides inside `subtotal`.
 */
export function computeInvoiceTotals(values: any): InvoiceTotals {
  const lines: any[] = values?.lines ?? [];
  const additionalLines: any[] = values?.additionalLines ?? [];

  const itemsSubtotal = lines.reduce((total: number, line: any) => total + lineTotal(line), 0);
  const additionalSubtotal = additionalLines.reduce((total: number, line: any) => total + lineTotal(line), 0);
  const subtotal = itemsSubtotal + additionalSubtotal;

  // Read, never derived from `taxPercent` — see the header.
  // No quantity multiplier: `create.ts` sums `item.taxAmount` as it stands
  // (`totalTaxAmount += LINES.reduce((t, i) => t + i.taxAmount, 0)`), and the
  // adapter hands every line a hardcoded 0 anyway.
  const taxTotal = lines.reduce((total: number, line: any) => total + num(line?.taxAmount), 0);

  const grossVat = lines.reduce((total: number, line: any) => total + lineVat(line), 0);

  // The base is lines PLUS hand-typed lines, because that is what
  // `create.ts` percentages against, and the clamp to it is the handler's own
  // `Math.min(totalDiscountAmount, linesTotalAmount)` — without it a 150%
  // discount would draw a negative total on screen.
  const rawDiscount = discountOff(subtotal, values?.discountType, values?.discountValue);
  // Clamped to [0, subtotal] in that order: `Math.min(x, subtotal)` alone would
  // hand back a NEGATIVE discount whenever the subtotal is negative.
  const discountAmount = Math.max(0, Math.min(rawDiscount, Math.max(0, subtotal)));

  // The allocator spreads the invoice-level discount across the lines in
  // proportion to their totals and then re-extracts each line's VAT from what
  // is left, so the VAT falls by exactly the same proportion.
  const vatTotal = subtotal > 0 ? grossVat * (1 - discountAmount / subtotal) : 0;

  const shippingAmount = num(values?.shippingAmount);

  return {
    itemsSubtotal,
    additionalSubtotal,
    subtotal,
    vatTotal,
    taxTotal,
    discountAmount,
    shippingAmount,
    grandTotal: subtotal - discountAmount + shippingAmount + taxTotal,
    lineCount: lines.length + additionalLines.length,
  };
}
